import { useQuery } from "@tanstack/react-query";
import { listEquipmentAssets, listEquipmentProtocols } from "@/lib/equipmentApi";
import { supabase } from "@/lib/supabase";
import type { EquipmentAsset, EquipmentProtocol } from "@/types/equipment";

export const EQUIPMENT_PROTOCOL_HISTORY_PATH = "/equipment/protocols";

const HISTORY_STATUSES = new Set<EquipmentProtocol["status"]>(["accepted", "rejected"]);

export type ProtocolHistoryKey = { id: string; name: string };

export interface EquipmentProtocolHistoryPayload {
  protocols: EquipmentProtocol[];
  names: Record<string, string>;
  assets: EquipmentAsset[];
  keys: ProtocolHistoryKey[];
}

function protocolSortTime(protocol: EquipmentProtocol): number {
  return new Date(protocol.responded_at ?? protocol.initiated_at).getTime();
}

export function useEquipmentProtocolHistory(orgId: string | null) {
  return useQuery({
    queryKey: ["equipment-protocol-history", orgId],
    enabled: Boolean(orgId),
    queryFn: async (): Promise<EquipmentProtocolHistoryPayload> => {
      if (!orgId) {
        return { protocols: [], names: {}, assets: [], keys: [] };
      }

      const [allProtocols, assets] = await Promise.all([
        listEquipmentProtocols({ orgId }),
        listEquipmentAssets(orgId),
      ]);

      const protocols = allProtocols
        .filter((item) => HISTORY_STATUSES.has(item.status))
        .sort((a, b) => protocolSortTime(b) - protocolSortTime(a));

      if (protocols.length === 0) {
        return { protocols: [], names: {}, assets, keys: [] };
      }

      const personIds = [
        ...new Set(
          protocols.flatMap((item) => [item.worker_id, item.initiated_by, item.responded_by].filter(Boolean)),
        ),
      ] as string[];
      const staffIds = [...new Set(protocols.map((item) => item.staff_equipment_id).filter(Boolean))] as string[];

      const [{ data: profiles, error: profileError }, keysResult] = await Promise.all([
        personIds.length > 0
          ? supabase.from("profiles").select("id, full_name, email").in("id", personIds)
          : Promise.resolve({ data: [] as { id: string; full_name: string | null; email: string | null }[], error: null }),
        staffIds.length > 0
          ? supabase.from("staff_equipment").select("id, name").in("id", staffIds)
          : Promise.resolve({ data: [] as ProtocolHistoryKey[], error: null }),
      ]);

      if (profileError) {
        console.error("[useEquipmentProtocolHistory] profiles", profileError);
        throw profileError;
      }
      if (keysResult.error) {
        console.error("[useEquipmentProtocolHistory] staff_equipment", keysResult.error);
        throw keysResult.error;
      }

      const names: Record<string, string> = {};
      for (const profile of profiles ?? []) {
        names[profile.id] = profile.full_name || profile.email || "Pracownik";
      }

      return {
        protocols,
        names,
        assets,
        keys: keysResult.data ?? [],
      };
    },
  });
}
